import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';
import { VIEWPORT_ONCE } from '../lib/motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export function GoogleLogo({ className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z"/>
      <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.6-5.9c-2.1 1.4-4.8 2.3-8 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
    </svg>
  );
}

export function Stars({ value, size = 'h-4 w-4' }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`${size} ${s <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'fill-none text-border'}`} />
      ))}
    </span>
  );
}

export function useGoogleReviews() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/google-reviews`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  return data;
}

export function GoogleReviewsSection({ data }) {
  if (!data || !data.enabled || !data.count) return null;
  const reviews = (data.reviews || []).slice(0, 3);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60" data-testid="google-reviews-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
          <div>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Avis Google</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Ils nous font confiance dans l'Artois</h2>
            <p className="text-muted-foreground max-w-xl">Des avis authentiques laissés par nos clients sur Google. La bienveillance et le professionnalisme de nos Easers, racontés par ceux qui les vivent.</p>
          </div>
          <Card className="p-5 flex items-center gap-4 border-border shadow-sm min-w-[260px]" data-testid="google-rating-card">
            <GoogleLogo className="h-10 w-10" />
            <div>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold leading-none" data-testid="google-rating-value">{Number(data.rating).toFixed(1).replace('.', ',')}</span>
                <Stars value={data.rating} size="h-5 w-5" />
              </div>
              <p className="text-xs text-muted-foreground mt-1" data-testid="google-rating-count">Basé sur {data.count} avis Google</p>
            </div>
          </Card>
        </motion.div>

        {reviews.length > 0 && (
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} data-testid="google-reviews-grid">
            {reviews.map((r, i) => (
              <motion.div key={`${r.author}-${i}`} variants={fadeUp}>
                <Card className="p-6 h-full flex flex-col border-border shadow-sm">
                  <Quote className="h-6 w-6 text-[hsl(var(--primary))]/30 mb-3" />
                  <Stars value={r.rating} />
                  <p className="text-sm text-muted-foreground leading-relaxed mt-3 mb-4 flex-1">« {r.text} »</p>
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] text-xs font-bold flex items-center justify-center">{(r.author || '?').charAt(0).toUpperCase()}</div>
                      <span className="text-sm font-medium">{r.author}</span>
                    </div>
                    <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><GoogleLogo className="h-3.5 w-3.5" />{r.date}</span>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {data.url && (
          <div className="text-center mt-10">
            <a href={data.url} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link">
              <Button variant="outline" size="lg">
                <GoogleLogo className="h-4 w-4 mr-2" /> Voir tous nos avis Google <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
